import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
} from "@mui/material";
import { Download, AutoAwesome, OpenInNew } from "@mui/icons-material";
import PropTypes from "prop-types";
import DataTable from "../DataTable.jsx";
import { useBookingExportActions } from "../../hooks/useBookingExportActions.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { backendGet, backendPost } from "../../services/dataService.js";
import { handleDownload } from "components/MDDForm/helper.js";
import BookingStatus from "components/BookingStatus.jsx";
import { getEuro, getDatumZeit } from "../../services/formatService.js";
import UserAvatar from "../UserAvatar.jsx";

// Sub-component for the detail panel
function ExportBookingsDetail({ exportItem }) {
  const { enqueueSnackbar } = useSnackbar();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!exportItem?.id) {
      return;
    }

    backendGet(`data/bookingexports/${exportItem.id}/bookings`, {
      label: "Exportierte Buchungen",
      setIsLoading: setLoading,
      setResult: (res) => setBookings(res.data),
      enqueueSnackbar,
    });
  }, [exportItem?.id]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Lade Buchungen...</Typography>
      </Box>
    );
  }

  if (!bookings || bookings.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Keine Buchungen gefunden.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Exportierte Buchungen
      </Typography>
      <Table size="small" sx={{ bgcolor: "#fff" }}>
        <TableHead>
          <TableRow>
            <TableCell>Empfänger</TableCell>
            <TableCell>IBAN</TableCell>
            <TableCell>BIC</TableCell>
            <TableCell>Beschreibung</TableCell>
            <TableCell>Betrag</TableCell>
            <TableCell>Status</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((b) => (
            <TableRow key={b.id}>
              <TableCell>{b.recipient}</TableCell>
              <TableCell>{b.iban}</TableCell>
              <TableCell>{b.bic}</TableCell>
              <TableCell>{b.description}</TableCell>
              <TableCell sx={{ color: "error.main", fontWeight: "bold" }}>
                -{getEuro(b.amount)}
              </TableCell>
              <TableCell>
                <BookingStatus booking={b} />
              </TableCell>
              <TableCell align="right">
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => {
                    window.open(`/schaden/${b.schaden_id}`, "_blank");
                  }}
                >
                  <OpenInNew fontSize="small" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

ExportBookingsDetail.propTypes = {
  exportItem: PropTypes.object,
};

export default function BookingExportsTable() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { state: bookingExportState, setState: setBookingExportState } =
    useBookingExportActions();

  const tableKey = "booking_exports";
  const storageKey = "v2_table_booking_exports";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const rows = bookingExportState?.data?.data || [];

  const renderDetailPanel = useCallback(
    ({ row }) => <ExportBookingsDetail exportItem={row.original} />,
    []
  );

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Buchungsexporte",
        route: "data/bookingexports",
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "created_at", order: "desc" }],
          include: ["created_by"],
        },
        onStateChange: setBookingExportState,
      },
    ],
    [tableKey, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
  }, []);

  const handleDownloadClick = (exportItem) => {
    backendGet(`data/bookingexports/${exportItem.id}/download`, {
      label: "Export-Download",
      setIsLoading: () => {},
      setResult: (res) => {
        if (res?.content_base64) {
          handleDownload({
            filename: res.filename,
            filedata: res.content_base64,
          });
        }
      },
      enqueueSnackbar,
    });
  };

  const handleSimulatePaymentClick = (exportItem) => {
    backendPost(`data/bookingexports/${exportItem.id}/simulate-payment`, {
      toSend: {},
      label: "Exportierte Zahlung simulieren",
      enqueueSnackbar,
      setResult: (res) => {
        if (res?.content_base64 && res?.filename) {
          handleDownload({
            filename: res.filename,
            filedata: res.content_base64,
          });
        }
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "export_identifier",
        header: "Export-Identifier",
        size: 150,
      },
      {
        accessorKey: "creator.full_name",
        header: "Erstellt von",
        size: 200,
        Cell: ({ row }) => {
          const creator = row.original.created_by;
          if (!creator) {
            return "-";
          }
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <UserAvatar user={creator} />
            </Box>
          );
        },
      },
      {
        accessorKey: "summe",
        header: "Summe",
        muiTableHeadCellProps: {
          align: "right",
        },
        size: 120,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ color: "error.main", fontWeight: "bold", textAlign: "right" }}
          >
            -{getEuro(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Erstellt am",
        size: 150,
        Cell: ({ cell }) => getDatumZeit(cell.getValue()),
      },
      {
        id: "actions",
        header: "Aktionen",
        size: 120,
        Cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadClick(row.original);
              }}
              title="Download XML"
            >
              <Download />
            </IconButton>
            {user?.role_names?.includes("sysadmin") && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSimulatePaymentClick(row.original);
                }}
                title="Zahlung simulieren"
              >
                <AutoAwesome />
              </IconButton>
            )}
          </Box>
        ),
      },
    ],
    [user]
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangePage={(pagination) => {
        const next =
          typeof pagination === "function"
            ? pagination({
                pageIndex: tableState.pageIndex,
                pageSize: tableState.pageSize,
              })
            : pagination;
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      renderDetailPanel={renderDetailPanel}
      enableExpandAll={false}
      initialState={{
        showGlobalFilter: true,
      }}
    />
  );
}
